import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import {
  REAL_NAME_STATUS,
  canSubmitKyc,
  isBasicAuthVerified,
  isKycVerified,
  normalizeRealNameStatus,
  resolveKycActionLabelKey,
  resolveKycStatusLabelKey,
} from '../../constants/kycConstants';
import { useTranslation } from '../../i18n/LocaleContext';

export default function KycHubPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [member, setMember] = useState(null);
  const [kycDetail, setKycDetail] = useState(null);
  const [submittedBanner] = useState(Boolean(location.state?.submitted));

  const basicVerified = useMemo(() => isBasicAuthVerified(member), [member]);
  const identityStatus = normalizeRealNameStatus(
    kycDetail?.realNameStatus || member?.realNameStatus
  );
  const canGoVerify = canSubmitKyc(identityStatus);
  const canViewContent = isKycVerified(identityStatus);
  const verifyActionKey = resolveKycActionLabelKey(identityStatus);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([memberApi.getInfo(), memberApi.getKycDetail()])
      .then(([info, detail]) => {
        if (!cancelled) {
          setMember(info || null);
          setKycDetail(detail || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || t('kyc.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  function handleBack() {
    const from = location.state?.from;
    if (from) {
      navigate(from);
      return;
    }
    navigate(-1);
  }

  function handleGoVerify() {
    if (!canGoVerify) {
      return;
    }
    navigate('/kyc/form', { state: { from: location.state?.from || '/kyc' } });
  }

  function handleViewContent() {
    navigate('/kyc/info');
  }

  return (
    <div className="deposit-page kyc-page">
      {submittedBanner && (
        <div className="kyc-page__banner">{t('kyc.submittedBanner')}</div>
      )}

      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={handleBack}
        >
          ‹
        </button>
        <h1>{t('kyc.hubTitle')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      {loading && <p className="deposit-page__status">{t('common.loading')}</p>}
      {error && <p className="deposit-page__error">{error}</p>}

      {!loading && !error && (
        <div className="kyc-hub">
          <section className="kyc-card">
            <div className="kyc-card__head">
              <h2>{t('kyc.basicTitle')}</h2>
              <span className={`kyc-card__status ${basicVerified ? 'is-verified' : ''}`}>
                {basicVerified ? '✓' : '○'}
                {t(basicVerified ? 'kyc.status.verified' : 'kyc.status.notCertified')}
              </span>
            </div>
            <p className="kyc-card__desc">{t('kyc.basicDesc')}</p>
          </section>

          <section className="kyc-card">
            <div className="kyc-card__head">
              <h2>{t('kyc.identityTitle')}</h2>
              <span className={`kyc-card__status kyc-card__status--${identityStatus.toLowerCase()}`}>
                {identityStatus === REAL_NAME_STATUS.VERIFIED ? '✓' : '○'}
                {t(resolveKycStatusLabelKey(identityStatus))}
              </span>
            </div>
            <p className="kyc-card__desc">{t('kyc.identityDesc')}</p>
            {identityStatus === REAL_NAME_STATUS.FAILED && kycDetail?.remark && (
              <p className="kyc-card__reject">{kycDetail.remark}</p>
            )}
            {canGoVerify && (
              <button type="button" className="kyc-card__action" onClick={handleGoVerify}>
                {t(verifyActionKey)}
              </button>
            )}
            {canViewContent && (
              <button
                type="button"
                className="kyc-card__action kyc-card__action--outline"
                onClick={handleViewContent}
              >
                {t('kyc.viewContent')}
              </button>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
